import { BCContextMenu, BCLoading, IContextMenuClickEventArgs } from '@bimser/components';
import { FormItemTypes, IDictionary, IItem, isNullOrUndefined, MLHelper } from '@bimser/core';
import GetIconByName from '@bimser/icons/lib/getIcon16ByName';
import React, { JSX } from 'react';
import { EContextMenuTargetTypes } from '../../../entities';
import {
	IContextMenuExecuteActionButton,
	IControlCaption,
	ICSSProperties,
	IPropertyField,
	MultiLanguageText
} from '../../../entities/common';
import { IUserEvent } from '../../../entities/services';
import Styles from '../assets/visualControlStyles.scss';
import CaptionWrapper from '../components/CaptionWrapper';
import ContextMenu from '../components/ContextMenu';
import { IContextMenuProperties } from '../components/ContextMenu/entities/IContextMenuProperties';
import { onActionButtonClick } from '../components/DataGrid/helpers/events/DataGridButtonEvents';
import {
	IContextMenuItemClickEventArgs,
	IContextMenuOnHiddenEventArgs,
	IContextMenuOnHidingEventArgs,
	IContextMenuOnShowingEventArgs,
	IContextMenuOnShownEventArgs
} from '../entities/EventArgs';
import { IVisualControlProperties } from '../entities/IVisualControlProperties';
import { getCultureEnumMembers } from '../helper';
import Control from './Control';

export default class VisualControl extends Control {
	loading: boolean = false;
	visible: boolean;
	clientVisible: boolean;
	enabled: boolean;
	clientEnabled: boolean;
	style: ICSSProperties;
	containerStyle: ICSSProperties;
	customClassName: string;
	caption: IControlCaption<MultiLanguageText>;
	title: MultiLanguageText;
	_showContextMenu: boolean;
	_clientX?: number;
	_clientY?: number;
	contextMenuKey?: string;
	contextMenuTarget?: EContextMenuTargetTypes;
	contextMenuColumnKey: string;
	__contextMenuTargetArgs: any;
	_hideCommonLoadingOverlay: boolean = false;

	constructor() {
		super();
		this.contextMenuKey = '';
		this.contextMenuTarget = EContextMenuTargetTypes.Container;
		this._showContextMenu = false;
		this._clientX = null;
		this._clientY = null;

		this.style = {
			backgroundColor: ''
		};
		this.containerStyle = {};
		this.customClassName = '';
		this.visible = true;
		this.clientVisible = true;
		this.enabled = true;
		this.clientEnabled = true;
		this.caption = {
			ellipsis: true,
			font: {
				bold: false,
				color: '#25241f',
				family: 'Source Sans Pro, sans-serif',
				italic: false,
				size: '13',
				strikethrough: false,
				underline: false
			},
			position: 'left',
			text: null,
			visible: true,
			size: {
				width: 120
			},
			horizontalAlign: 'left',
			verticalAlign: 'middle',
			showColon: false,
			markSettings: {
				char: null,
				position: 'atFirst',
				style: {}
			}
		};
		this.__onShowContextMenu = this.__onShowContextMenu.bind(this);
		this.__onHiddingContextMenu = this.__onHiddingContextMenu.bind(this);
		this.__onClickContextMenu = this.__onClickContextMenu.bind(this);
		this.subscribe('trigger:OnClick', (args: any) => {
			this.__onControlClick(args);
		});

		this.subscribe('trigger:OnDoubleClick', (args: any) => {
			this.__onControlDoubleClick(args);
		});
	}

	__getMLClassPath() {
		return [...super.__getMLClassPath(), 'title', 'caption.text'];
	}

	initialize() {
		super.initialize();

		this.caption.text = new MultiLanguageText(
			(text: string, culture: string, existCultures: string[]) => {
				this.__onChangeMLTextValue(text, 'caption.text', culture, existCultures);
			}
		);

		this.title = new MultiLanguageText(
			(text: string, culture: string, existCultures: string[]) => {
				this.__onChangeMLTextValue(text, 'title', culture, existCultures);
			}
		);
	}

	showLoading() {
		this.__proxyChanging.loading = true;
	}

	hideLoading() {
		this.__proxyChanging.loading = false;
	}

	toggleLoading() {
		this.__proxyChanging.loading = !this.loading;
	}

	__loadProperties(data: IVisualControlProperties) {
		super.__loadProperties(data);

		const loadStyle: ICSSProperties = {};
		if (data.style) {
			Object.keys(data.style).forEach(key => {
				if (!isNullOrUndefined(data.style[key]) && data.style[key] != '') {
					loadStyle[key] = data.style[key] as any;
				}
			});
		}

		if (data?.title) {
			if (this.title?.setData) {
				this.title.setData(data.title);
			}
		}

		this.__proxyChanged.visible = data.visible;
		this.__proxyChanged.clientVisible = data.clientVisible;
		this.__proxyChanged.enabled = data.enabled;
		this.__proxyChanged.clientEnabled = data.clientEnabled;
		this.__proxyChanged.style = loadStyle;
		this.__proxyChanged.containerStyle = data.containerStyle;
		this.__proxyChanged.customClassName = data.customClassName;
		this.__proxyChanged.loading = data.loading;
		this.__proxyChanged.contextMenuKey = data.contextMenuKey;
		this.__proxyChanged.contextMenuTarget = data.contextMenuTarget;
		this.__proxyChanged.contextMenuColumnKey = data.contextMenuColumnKey;
		this.__proxyChanged._showContextMenu = data._showContextMenu;
		this.__proxyChanged._clientX = data._clientX;
		this.__proxyChanged._clientY = data._clientY;

		if (data.caption) {
			this.__proxyChanged.caption.position = data.caption.position;
			this.__proxyChanged.caption.size = data.caption.size;
			this.__proxyChanged.caption.font = data.caption.font;
			this.__proxyChanged.caption.visible = data.caption.visible;
			this.__proxyChanged.caption.ellipsis = data.caption.ellipsis;
			this.__proxyChanged.caption.horizontalAlign = data.caption.horizontalAlign;
			this.__proxyChanged.caption.verticalAlign = data.caption.verticalAlign;
			this.__proxyChanged.caption.style = data.caption.style;
			this.__proxyChanged.caption.markSettings = data.caption.markSettings;
			this.__proxyChanged.caption.showColon = data.caption.showColon;

			this.caption.text.setData(data.caption.text);
		}

		if (data.title) {
			this.title.setData(data.title);
		}
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();
		return [
			...fields,
			{
				name: 'visible',
				type: 'boolean'
			},
			{
				name: 'clientVisible',
				type: 'boolean',
				common: true
			},
			{
				name: 'enabled',
				type: 'boolean'
			},
			{
				name: 'clientEnabled',
				type: 'boolean',
				common: true
			},
			{
				name: 'contextMenuKey',
				type: 'string'
			},

			{
				name: 'title',
				type: 'object',
				items: getCultureEnumMembers(true)
			}
		];
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			caption: {
				...this.caption,
				text: this.caption?.text?.getText && this.caption?.text?.getText()
			},
			title: this.title ? this.title.getData() : {},
			loading: this.loading,
			visible: this.visible && this.clientVisible,
			style: this.style,
			containerStyle: this.containerStyle,
			customClassName: this.customClassName,
			disabled: this.loading || this.__isDisabled(),
			clientVisible: this.clientVisible,
			enabled: this.enabled,
			clientEnabled: this.clientEnabled
		};
	}

	__renderLoadingOverlay(children: React.ReactNode) {
		if (this._hideCommonLoadingOverlay) {
			return children;
		}

		return (
			<BCLoading
				show={this.loading}
				size="small"
			>
				{children}
			</BCLoading>
		);
	}

	__getContextInstance(): ContextMenu {
		return (this.__form as any)[this.contextMenuKey];
	}
	__onClickContextMenu(args: IContextMenuClickEventArgs) {
		const contextMenu = this.__getContextInstance();

		if (contextMenu) {
			const actionButton = contextMenu.items.find(
				i => args.data.key === i.name && (i.typeName as any) !== 'ContextItemArgs'
			);
			if (actionButton) {
				onActionButtonClick.call(contextMenu, actionButton);
			}
			const eventArgs: IContextMenuItemClickEventArgs = {
				baseArgs: args,
				targetArgs: this.__contextMenuTargetArgs,
				targetControlName: this.name
			};
			contextMenu.emit('event:OnItemClick', eventArgs);
			contextMenu.emit<IUserEvent<IContextMenuItemClickEventArgs>>(
				'event:server:OnItemClick',
				{
					argument: {
						baseArgs: {
							data: eventArgs.baseArgs.data
						},
						targetArgs: {
							row: eventArgs.targetArgs?.row?.cells.map(
								({ column, text, value }) => ({
									name: column.name,
									text,
									value
								})
							)
						},
						targetControlName: eventArgs.targetControlName
					},
					controlId: contextMenu.name,
					name: 'ContextMenuItemClick',
					sender: 'web'
				}
			);
		}
	}
	__hideOtherContexts() {
		this.__form.items.forEach((x, key) => {
			if (x?.properties?.get('_showContextMenu') && key != this.name) {
				if ((this.__form as any)[key]) {
					((this.__form as any)[key] as VisualControl).__onHiddingContextMenu(false);
				}
			}
		});
	}

	__onShowContextMenu(mouseEvent: React.MouseEvent<MouseEvent>, args: any) {
		const contextMenu = this.__getContextInstance();
		if (contextMenu && !this.__form.readOnly) {
			const eventArgs: IContextMenuOnShowingEventArgs = {
				cancel: false,
				targetArgs: args,
				targetControlName: this.name,
				contextMenuItems: contextMenu.items,
				updatedContextMenuItems: false
			};
			contextMenu.emit('event:OnShowing', eventArgs).then(() => {
				if (!eventArgs.cancel) {
					this.__hideOtherContexts();
					if (eventArgs.updatedContextMenuItems) {
						contextMenu.__proxyChanging.items = eventArgs.contextMenuItems;
					}

					if (this.contextMenuTarget === EContextMenuTargetTypes.Column) {
						let element = mouseEvent.target as HTMLButtonElement;
						const clientRect = element.getBoundingClientRect();
						this.__proxyChanging._clientX = clientRect.right - clientRect.width - 5;
						this.__proxyChanging._clientY = clientRect.bottom;
					} else {
						this.__proxyChanging._clientX = mouseEvent.clientX;
						this.__proxyChanging._clientY = mouseEvent.clientY;
					}

					this.__proxyChanging._showContextMenu = true;
					this.__contextMenuTargetArgs = args;
					contextMenu.emit('event:OnShown', {
						targetControlName: this.name
					} as IContextMenuOnShownEventArgs);
				}
			});
		}
	}

	__checkStatusOfParentControlEnabled(name: string = this.name): boolean {
		if (this.controlType === FormItemTypes.EContainerTypes.Form) {
			return false;
		}

		const parentId: string = this.__form.view.getIn(['entities', 'items', name, 'parentId']);
		const parentControl = this.__form.items.get(parentId);

		if (!parentId || !parentControl) {
			return true;
		}

		if (
			![
				FormItemTypes.EControlTypes.Collapse,
				FormItemTypes.EControlTypes.Tabs,
				FormItemTypes.EControlTypes.InputGroup,
				FormItemTypes.EContainerTypes.Form,
				FormItemTypes.EControlTypes.Panel,
				FormItemTypes.EControlTypes.ScrollView
			].includes(parentControl.type as FormItemTypes.EControlTypes)
		) {
			return this.__checkStatusOfParentControlEnabled(parentControl.id);
		}

		if (parentControl.type === FormItemTypes.EControlTypes.Tab) {
			const parentControlsParentId: string = this.__form.view.getIn([
				'entities',
				'items',
				parentControl.id,
				'parentId'
			]);

			const controlEnabled: boolean = parentControl.getIn(['properties', 'enabled']);
			const controlClientEnabled: boolean = parentControl.getIn([
				'properties',
				'clientEnabled'
			]);
			const tabsControlEnabled: boolean = this.__form.items.getIn([
				parentControlsParentId,
				'properties',
				'enabled'
			]);
			const tabsControlClientEnabled: boolean = this.__form.items.getIn([
				parentControlsParentId,
				'properties',
				'clientEnabled'
			]);

			return ![
				controlEnabled,
				controlClientEnabled,
				tabsControlEnabled,
				tabsControlClientEnabled
			].some((value: boolean) => {
				if (!isNullOrUndefined(value) && !value) {
					return true;
				}
			});
		}

		const enabled: boolean = parentControl.getIn(['properties', 'enabled']);
		const clientEnabled: boolean = parentControl.getIn(['properties', 'clientEnabled']);

		if (!isNullOrUndefined(enabled) && !isNullOrUndefined(clientEnabled)) {
			return enabled && clientEnabled;
		} else if (!isNullOrUndefined(enabled) && isNullOrUndefined(clientEnabled)) {
			return enabled;
		} else if (isNullOrUndefined(enabled) && !isNullOrUndefined(clientEnabled)) {
			return clientEnabled;
		}

		return true;
	}

	__isDisabled() {
		if (this.__form) {
			return (
				!this.enabled ||
				!this.clientEnabled ||
				!this.__form.enabled ||
				!this.__form.clientEnabled ||
				!this.__checkStatusOfParentControlEnabled()
			);
		}
	}

	__onHiddingContextMenu(visible?: boolean) {
		const contextMenu = this.__getContextInstance();
		if (contextMenu) {
			const eventArgs: IContextMenuOnHidingEventArgs = {
				cancel: false,
				targetArgs: this.__contextMenuTargetArgs,
				targetControlName: this.name
			};
			contextMenu.emit('event:OnHiding', eventArgs).then(() => {
				if (!eventArgs.cancel) {
					this.__proxyChanging._showContextMenu = visible;
					contextMenu.emit('event:OnHidden', {
						targetArgs: this.__contextMenuTargetArgs,
						targetControlName: this.name
					} as IContextMenuOnHiddenEventArgs);
				}
			});
		}
	}

	__getChildsByItemType(item: IContextMenuExecuteActionButton, backgroundColor: string) {
		if ((item.typeName as any) === 'ContextItemArgs') {
			return this.__getContextMenuItems(item.args.children, backgroundColor) || [];
		}
		return [];
	}

	__getContextMenuItems(
		items: Array<IContextMenuExecuteActionButton>,
		backgroundColor: string
	): Array<IItem> {
		const setStyle = (x: IItem) => {
			if (backgroundColor) {
				x.style = { backgroundColor: backgroundColor, margin: '0px' };
			}
			return x;
		};
		return [
			...(items
				?.filter(item => item.visible)
				?.map(i =>
					setStyle({
						text: MLHelper.getMLText(i.text),
						key: i.name,
						icon: i.icon ? GetIconByName(i.icon)?.info : null,
						title: MLHelper.getMLText(i.title),
						items: this.__getChildsByItemType(i, backgroundColor)
					} as IItem)
				) || [])
		];
	}

	__renderContextMenu() {
		if (
			this.contextMenuKey &&
			this.__form.items.find(i => i.id === this.contextMenuKey) &&
			this._showContextMenu
		) {
			const contextMenu = this.__form.items.find(i => i.id === this.contextMenuKey).toJS();
			const contextMenuProperties: IContextMenuProperties = contextMenu.properties;
			let marginX: number;
			let marginY: number;

			if (this.__form.displayMode === 'preview') {
				marginX = -30;
				marginY = -95;
			}
			return (
				<BCContextMenu
					key={this.contextMenuKey}
					className={
						Styles.contextMenuContainer +
						(contextMenuProperties.customClassName
							? ' ' + contextMenuProperties.customClassName
							: '')
					}
					{...(contextMenuProperties.style?.backgroundColor
						? {
								menuStyle: {
									backgroundColor: contextMenuProperties.style?.backgroundColor
								}
						  }
						: {})}
					useStateForOpenKeys
					forceSubMenuRender
					clientX={this._clientX}
					clientY={this._clientY}
					marginX={marginX}
					marginY={marginY}
					visible={this._showContextMenu}
					isLoading={contextMenuProperties?.loading}
					items={this.__getContextMenuItems(
						contextMenuProperties?.items,
						contextMenuProperties?.style?.backgroundColor
					)}
					onClick={this.__onClickContextMenu}
					onClose={this.__onHiddingContextMenu}
					closeWhenAnyScroll
					keepOpenParents
					triggerMethod={
						this.contextMenuTarget === EContextMenuTargetTypes.Column
							? 'click'
							: 'context'
					}
					popupContainer={this.__form.__getShadowRootScrollable()}
				/>
			);
		}
	}

	__render(children?: React.ReactNode): JSX.Element {
		const isControlVisible =
			(this.visible && this.clientVisible) ||
			this.controlType === FormItemTypes.EContainerTypes.Form;
		if (isControlVisible) {
			return super.__render(
				<CaptionWrapper
					key={this.name}
					caption={this.caption}
					containerStyle={this.containerStyle}
					customClassName={this.customClassName}
					controlType={this.controlType}
					controlName={this.name}
					title={this.title}
					onClick={this.__onControlClick}
					onDoubleClick={this.__onControlDoubleClick}
					currentLanguage={this.__form?.currentLanguage}
					windowId={this.__form?.__windowId || ''}
					activeView={this.__form?.activeView || ''}
				>
					{this.__renderLoadingOverlay(children)}
					{this.__renderContextMenu()}
				</CaptionWrapper>
			);
		}

		this.__setControlProperties();
		return <></>;
	}
}
